import { computed, ref, watch } from 'vue'
import { api } from '@/global/services/api'
import { isArray, isFunction } from '@/global/utils/utils'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTaskStreetPaths } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskStreetPaths'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  buildTaskStreetsEndpoint,
  TASKS_TRANSLATION_NAMESPACE as NS,
  TASK_STREETS_TABLE_KEY
} from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

/**
 * The route tab (Popis ulica) of a task: the streets it covers, listed beside the map
 * they were drawn on.
 *
 * The tab drives its own requests rather than leaning on the tab layout's save: the
 * street endpoints take one street at a time, and each is settled by its own dialog
 * the moment it is confirmed. There is therefore no edit mode to switch here - drawing
 * a street, opening one and deleting one are offered for as long as the tab is open,
 * and the screen header keeps its buttons to itself while it is (see TaskDetails.vue).
 */
export function useTaskStreets (options) {
  const {
    translate,
    taskId,
    openStreetDialogFn,
    openInfoDialogFn,
    closeInfoDialogFn,
    setInfoDialogLoaderFn
  } = options || {}

  const { buildConfig } = useTableService()
  const { forgetStreets, streetsChangedAt } = useTaskStreetPaths()

  // What the table has listed, kept so the map draws the same set without a request of
  // its own.
  const streets = ref([])

  const endpoint = computed(() => buildTaskStreetsEndpoint(taskId))

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TASK_STREETS_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }

    fetchData()
  }

  /**
   * Says that what is known about this task's streets is out of date. The maps
   * elsewhere on the screen remember them rather than fetching per view, so anything
   * written here has to reach them; the table is refetched by the watcher below, which
   * is the same path a change made in another tab takes.
   */
  const refreshTableData = () => {
    forgetStreets([taskId])
  }

  // A street changed somewhere on the screen - written here, or the task's colour
  // saved in the tab next door, which is what its streets are drawn in. Either way the
  // rows this tab is holding are behind.
  watch(streetsChangedAt, fetchTableData)

  // The map draws exactly what the table has listed, so it is fed off the same
  // response rather than fetching the route a second time.
  const onDataReceived = (apiData) => {
    streets.value = isArray(apiData?.data) ? apiData.data : []
  }

  /**
   * Opens a street. Reached two ways - from its row in the table, which hands over a
   * table event, and from its route on the map, which hands over the street itself -
   * so both shapes are accepted.
   */
  const onOpenStreet = (event) => {
    const street = event?.data ?? event

    if (!street?.id || !isFunction(openStreetDialogFn)) {
      return
    }

    openStreetDialogFn({
      title: translate(`${NS}.edit_street`),
      street,
      drawnPath: null,
      readonly: false
    })
  }

  /**
   * A street is created by drawing it: the map hands over the shape it measured, and
   * the dialog collects what is written about it.
   */
  const onStreetDrawn = (drawnPath) => {
    if (!isFunction(openStreetDialogFn)) {
      return
    }

    openStreetDialogFn({
      title: translate(`${NS}.new_street`),
      street: null,
      drawnPath
    })
  }

  const deleteStreet = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      setInfoDialogLoaderFn?.(true)
      await api(WINTER_SERVICE_MODULE).delete(`${endpoint.value}/${id}`)
      closeInfoDialogFn?.()
      refreshTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn?.(false)
    }
  }

  const onStreetDelete = (event) => {
    if (!isFunction(openInfoDialogFn)) {
      return
    }

    openInfoDialogFn({
      title: translate(`${NS}.street_delete_confirmation_title`),
      message: translate(`${NS}.street_delete_confirmation_message`),
      onConfirm: () => deleteStreet(event)
    })
  }

  // The listing reports the path type as its own object and leaves a polygon's length,
  // width and passes empty - the columns say so rather than showing a blank.
  const cellsConfig = computed(() => ({
    path_type: {
      custom_display_value: (data) => data?.path_type?.description || data?.path_type?.name || ''
    },
    passes: {
      custom_display_value: (data) => data?.passes ?? '-'
    },
    length: {
      custom_display_value: (data) => data?.length ?? '-'
    },
    width: {
      custom_display_value: (data) => data?.width ?? '-'
    },
    area: {
      custom_display_value: (data) => data?.area ?? '-'
    }
  }))

  const headers = computed(() => ([
    {
      label: translate(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: translate(`${NS}.street_type`),
      key: 'path_type',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.street_name`),
      key: 'street_name',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.passes`),
      key: 'passes',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.length_m`),
      key: 'length',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.width_m`),
      key: 'width',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.area_m2`),
      key: 'area',
      sortable: true,
      always_visible: true
    },
    {
      label: translate(`${NS}.city_district`),
      key: 'street_district',
      sortable: true,
      always_visible: true
    }
  ]))

  // Both actions stand for as long as the tab is open - there is no edit mode here to
  // unlock the second one.
  const actionsConfig = computed(() => ([
    showDetailsAction(translate, onOpenStreet),
    deleteAction(translate, onStreetDelete)
  ]))

  const tableConfig = computed(() => buildConfig({
    key: TASK_STREETS_TABLE_KEY,
    // The table lives inside a tab, not on a screen of its own - it fills the tab and
    // scrolls its rows internally.
    fillHeight: true,
    apiConfig: {
      get: {
        module: WINTER_SERVICE_MODULE,
        route: endpoint.value
      }
    },
    onDataReceived,
    // Clicking the row opens the street, the same way clicking its route on the map
    // does - the action menu keeps it reachable either way.
    rowDblClick: onOpenStreet,
    // `search_and_buttons_config` is deliberately left out rather than emptied: the
    // row renders as soon as the key is there, and there is nothing to put in it - a
    // street is drawn on the map rather than searched for or typed into a form.
    // Nothing in the row below it applies either: the tab shows one task's whole
    // route, so there is nothing to filter or refresh by hand, and the columns are
    // the street's own handful.
    filters_and_columns_visibility_config: {
      showRefreshButton: false,
      showFiltersButton: false,
      showColumnsVisibilityButton: false,
      showTableGridSwitchButton: false
    },
    globalStyle: {
      // With no toolbar row above it the table starts at the container's own padding,
      // which left it sitting lower than the map next to it.
      containerStyle: {
        padding: 0
      }
    },
    tableConfig: {
      cellsConfig: cellsConfig.value,
      headers: headers.value,
      actionsConfig: actionsConfig.value
    }
  }))

  return {
    streets,
    tableConfig,
    refreshTableData,
    onStreetDrawn,
    onOpenStreet
  }
}
